import moment from 'moment';
import {
  TenancyEndDateTypeOption,
  TenancyLengthOptions,
  TenancyLengthTypeOption,
  TenancyLengthUnitType,
  TenancyOptionRateTypeOption,
  TenancyStartDateTypeOption,
  TenancyTypeOption,
} from '~graphql/__generated__/globalTypes';

export const DATE_FORMAT = 'll';

export const momentAddDays = (date: Date, count: string | number) => {
  return moment(date)
    .add(count, 'days')
    .toDate();
};

export const momentSubtractDays = (date: Date, count: string | number) => {
  return moment(date)
    .subtract(count, 'days')
    .toDate();
};

export const isAfter = (date: Date | number, dateToCompare: Date | number) => {
  return moment(date).diff(moment(dateToCompare), 'days') > 0;
};

export const isSameDay = (date: Date | number, dateToCompare: Date | number) => {
  return moment(date).diff(moment(dateToCompare), 'days') === 0;
};

export interface TenancyOption {
  endDate: string;
  endDateType: TenancyEndDateTypeOption;
  id: string;
  name: string;
  startDate: string;
  startDateType: TenancyStartDateTypeOption;
  tenancyLength: number;
  tenancyLengthType: TenancyLengthTypeOption;
  tenancyType: TenancyTypeOption;
  rateType: TenancyOptionRateTypeOption;
  tenancyLengthOption: TenancyLengthOptions;
  tenancyLengthUnit: TenancyLengthUnitType;
}

export const getTenancyOptionRangeDayPickerStartDisabledDays = (tenancyOption: TenancyOption) => {
  const {
    startDate,
    startDateType,
    endDate,
    endDateType,
    tenancyLength,
    tenancyLengthType,
  } = tenancyOption;
  const currentDateIsAfterTenancyStartDate = moment().isSameOrAfter(moment(startDate));
  const parseStartDate = currentDateIsAfterTenancyStartDate
    ? moment().toDate()
    : moment(startDate).toDate();
  const parseEndDate = moment(endDate).toDate();
  const isEQStartDateType = startDateType === TenancyStartDateTypeOption.EQ;
  const isEQEndDateType = endDateType === TenancyEndDateTypeOption.EQ;
  const isEQTenancyLengthType = tenancyLengthType === TenancyLengthTypeOption.EQ;
  const isLETenancyLengthType = tenancyLengthType === TenancyLengthTypeOption.LE;
  let disabledDaysRules: any[] = [{ before: parseStartDate }, { after: parseEndDate }];

  if (isEQStartDateType) {
    disabledDaysRules.push({
      from: momentAddDays(parseStartDate, isLETenancyLengthType ? tenancyLength + 1 : 1),
      to: isLETenancyLengthType ? parseEndDate : momentAddDays(parseStartDate, tenancyLength - 1),
    });
    return disabledDaysRules;
  }

  if (isEQEndDateType) {
    const disabledBefore = isLETenancyLengthType
      ? momentSubtractDays(parseEndDate, tenancyLength)
      : parseStartDate;
    const disabledAfter = momentSubtractDays(
      parseEndDate,
      isLETenancyLengthType ? 1 : tenancyLength
    );
    disabledDaysRules = [{ before: disabledBefore }, { after: disabledAfter }];
    return disabledDaysRules;
  }

  if (isEQTenancyLengthType) {
    disabledDaysRules = [
      { before: parseStartDate },
      { after: momentSubtractDays(parseEndDate, tenancyLength) },
    ];
    return disabledDaysRules;
  }
  return disabledDaysRules;
};

export const getTenancyOptionRangeDayPickerEndDisabledDays = (
  tenancyOption: TenancyOption,
  rangeDateFrom: Date | undefined
) => {
  const { startDateType, endDate, endDateType, tenancyLength, tenancyLengthType } = tenancyOption;
  const parseStartDate = moment(rangeDateFrom).toDate();
  const parseEndDate = moment(endDate).toDate();
  const isEQStartDateType = startDateType === TenancyStartDateTypeOption.EQ;
  const isEQEndDateType = endDateType === TenancyEndDateTypeOption.EQ;
  const isLETenancyLengthType = tenancyLengthType === TenancyLengthTypeOption.LE;
  const isGETenancyLengthType = tenancyLengthType === TenancyLengthTypeOption.GE;
  let disabledDaysRules: any[] = [{ before: parseStartDate }, { after: parseEndDate }];

  if (isEQStartDateType && rangeDateFrom) {
    const disabledBefore = momentAddDays(parseStartDate, isLETenancyLengthType ? 1 : tenancyLength);
    const disabledAfter = isLETenancyLengthType
      ? momentAddDays(parseStartDate, tenancyLength)
      : parseEndDate;
    disabledDaysRules = [{ before: disabledBefore }, { after: disabledAfter }];
    return disabledDaysRules;
  }

  if (isEQEndDateType) {
    const disabledBefore = parseEndDate;
    const disabledAfter = parseEndDate;
    return [{ before: disabledBefore }, { after: disabledAfter }];
  }

  if (isLETenancyLengthType && rangeDateFrom) {
    const parseRangeDateFrom = moment(rangeDateFrom).toDate();

    const tenancyLengthDisabledDaysBeforeSeletedDate = {
      from: momentSubtractDays(parseStartDate, 1),
      to: momentSubtractDays(parseRangeDateFrom, tenancyLength + 1),
    };
    const tenancyLengthDisabledDaysAfterSeletedDate = {
      from: momentAddDays(parseRangeDateFrom, tenancyLength + 1),
      to: parseEndDate,
    };

    const tenancyLengthDisabledRangeDateFrom = parseRangeDateFrom;

    disabledDaysRules.push(
      tenancyLengthDisabledDaysBeforeSeletedDate,
      tenancyLengthDisabledDaysAfterSeletedDate,
      tenancyLengthDisabledRangeDateFrom
    );

    return disabledDaysRules;
  }

  if (isGETenancyLengthType && rangeDateFrom) {
    const parseRangeDateFrom = moment(rangeDateFrom).toDate();

    const tenancyLengthDisabledDaysBeforeSeletedDate = {
      from: momentSubtractDays(parseRangeDateFrom, tenancyLength - 1),
      to: momentAddDays(parseRangeDateFrom, tenancyLength - 1),
    };

    disabledDaysRules.push(tenancyLengthDisabledDaysBeforeSeletedDate);
    return disabledDaysRules;
  }
  return disabledDaysRules;
};

type TenancyOptionInfosTenancyOption = {
  endDate: any | null;
  endDateType: TenancyEndDateTypeOption | null;
  id: string | null;
  name: string;
  originId: number | null;
  startDate: any | null;
  startDateType: TenancyStartDateTypeOption | null;
  tenancyLength: any | null;
  tenancyLengthType: TenancyLengthTypeOption | null;
  tenancyType: TenancyTypeOption;
  rateType: TenancyOptionRateTypeOption;
  tenancyLengthOption: TenancyLengthOptions;
};

export type AvailableTenancyOptionUnit = {
  selectableStartDates: string[];
  tenancyOption: {
    id: string | null;
    originId: number | null;
    name: string;
    rateType: TenancyOptionRateTypeOption;
    tenancyLengthType: TenancyLengthTypeOption | null;
    tenancyType: TenancyTypeOption;
    startDate: any | null;
    endDate: any | null;
    startDateType: TenancyStartDateTypeOption | null;
    endDateType: TenancyEndDateTypeOption | null;
    tenancyLength: any | null;
    tenancyLengthOption: TenancyLengthOptions;
    tenancyLengthUnit: TenancyLengthUnitType;
    academicYear: {
      id: string | null;
      originId: number | null;
      name: string;
      fromYear: number | null;
      toYear: number | null;
    };
  };
};

export interface TenancyOptionsListItem {
  academicYear: { id: string; name: string };
  tenancyOptions: (TenancyOption & { selectableStartDates: string[] })[];
}

export function mapAvailableTenancyOptionsToList(
  availableTenancyOptions?: AvailableTenancyOptionUnit[] | null
): TenancyOptionsListItem[] {
  if (!availableTenancyOptions) return [];

  const groupMap = new Map<string, TenancyOptionsListItem>();

  for (const item of availableTenancyOptions) {
    const { tenancyOption, selectableStartDates } = item;
    const ay = tenancyOption.academicYear;
    if (!ay?.id || !tenancyOption.id) continue;
    if (
      !tenancyOption.tenancyLengthType ||
      !tenancyOption.startDateType ||
      !tenancyOption.endDateType
    )
      continue;

    const mapped: TenancyOption & { selectableStartDates: string[] } = {
      id: tenancyOption.id,
      name: tenancyOption.name,
      rateType: tenancyOption.rateType,
      tenancyLengthType: tenancyOption.tenancyLengthType,
      tenancyType: tenancyOption.tenancyType,
      startDate: tenancyOption.startDate,
      endDate: tenancyOption.endDate,
      startDateType: tenancyOption.startDateType,
      endDateType: tenancyOption.endDateType,
      tenancyLength: tenancyOption.tenancyLength,
      tenancyLengthOption: tenancyOption.tenancyLengthOption,
      tenancyLengthUnit: tenancyOption.tenancyLengthUnit,
      selectableStartDates: selectableStartDates ?? [],
    };

    const existing = groupMap.get(ay.id);
    if (existing) {
      existing.tenancyOptions.push(mapped);
    } else {
      groupMap.set(ay.id, {
        academicYear: { id: ay.id, name: ay.name },
        tenancyOptions: [mapped],
      });
    }
  }

  for (const group of groupMap.values()) {
    group.tenancyOptions.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
  }

  return Array.from(groupMap.values());
}

export default getTenancyOptionRangeDayPickerStartDisabledDays;
